import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'react-bootstrap-icons';

import useQueryParameters from '../../hooks/use_query_parameters';
import useTranslation from '../../hooks/use_translation';
import useWindowSize from '../../hooks/use_window_resize';
import TestGroups from './groups';

const filtersConfig = [
	{
		filter_type: 'bool',
		filters: [{ key: 'discount', label: 't1' }],
		groupName: 't2',
	},
	{
		filter_type: 'array',
		filters: [
			{ key: 'product_type', label: 't10', value: 'single' },
			{ key: 'product_type', label: 't11', value: 'checkup' },
			{ key: 'product_type', label: 't12', value: 'package' },
			{ key: 'product_type', label: 't14', value: 'self_test' },
		],
		groupName: 't13',
	},
	{
		filter_type: 'array',
		filters: [
			{
				key: 'product_settings',
				label: 't16',
				value: 'include_biological_age',
			},
			{
				key: 'product_settings',
				label: 't17',
				value: 'include_statement',
			},
		],
		groupName: 't15',
	},
];

const TestFilters = ({ group_id }) => {
	const { queryParameters } = useQueryParameters();
	const [isMobile, setIsMobile] = useState(false);
	const { width } = useWindowSize();
	const [activeAccordion, setActiveAccordion] = useState(null);

	useEffect(() => {
		setIsMobile(width < 768);
	}, [width]);

	const filtersUsedCount = useMemo(() => {
		let count = 0;

		filtersConfig.forEach((group) => {
			group.filters.forEach((filter) => {
				if (!queryParameters?.[filter.key]) return;

				if (
					(group.filter_type == 'array' &&
						queryParameters?.[filter.key].includes(filter.value)) ||
					group.filter_type == 'bool'
				) {
					count++;
				}
			});
		});

		return count;
	}, [queryParameters]);

	const types = {
		category: { label: 't6' },
		filter: { label: 't3' },
	};

	const handleTypeClick = (type) => {
		setActiveAccordion(type);
	};

	return (
		<>
			{isMobile ? (
				<>
					<div className="container d-flex gap-1">
						{Object.keys(types).map((type) => (
							<FilterButton
								filtersUsedCount={filtersUsedCount}
								key={type}
								label={types[type].label}
								onClick={() => handleTypeClick(type)}
								type={type}
							/>
						))}
					</div>
					<FilterModal>
						{filtersUsedCount > 0 && (
							<ChosenFilters
								filtersUsedCount={filtersUsedCount}
								isMobile={isMobile}
							/>
						)}
						<TestGroups activeAccordion={activeAccordion} group_id={group_id} />

						<FiltersList activeAccordion={activeAccordion} />
					</FilterModal>
				</>
			) : (
				<>
					{filtersUsedCount > 0 && (
						<ChosenFilters filtersUsedCount={filtersUsedCount} />
					)}
					<TestGroups activeAccordion={'category'} group_id={group_id} />
					<FiltersList activeAccordion={activeAccordion} group_id={group_id} />
				</>
			)}
		</>
	);
};

const FilterButton = ({ filtersUsedCount, label, onClick, type }) => {
	const { t } = useTranslation('component_test_filters');

	return (
		<button
			className="btn btn-light mb-3 rounded-pill fw-700 xs w-100"
			data-bs-target="#staticBackdrop"
			data-bs-toggle="offcanvas"
			onClick={onClick}
			type="button"
		>
			{t(label)}
			{filtersUsedCount > 0 && type === 'filter' && (
				<>
					{'('} {filtersUsedCount} {')'}
				</>
			)}
		</button>
	);
};

const ResetFilterButton = () => {
	const { resetQueryParameters } = useQueryParameters();
	const { t } = useTranslation('component_test_filters');

	const handleReset = () => {
		let keys = [];

		filtersConfig.forEach((group) => {
			group.filters.forEach((filter) => {
				keys.push(filter.key);
			});
		});

		resetQueryParameters(keys);
	};

	return (
		<button
			className="btn btn-light fw-600  w-100 d-flex justify-content-between align-items-center"
			onClick={handleReset}
			type="button"
		>
			<span className="xs"> {t('t4')}</span>
			<X className="" />
		</button>
	);
};

const ChosenFilters = ({ filtersUsedCount, isMobile }) => {
	const { t } = useTranslation('component_test_filters');

	return (
		<>
			{isMobile ? (
				<>
					<div className="d-flex gap-1">
						<button
							className="btn btn-dark fw-600 xs w-100"
							data-bs-dismiss="modal"
							type="button"
						>
							{t('t5')} {'('} {filtersUsedCount} {')'}
						</button>

						<ResetFilterButton />
					</div>
				</>
			) : (
				<>
					<h4
						className="accordion-header accordion-button-fake pt-0 d-flex pb-4 text-dark text-decoration-none sm fw-700"
						id="information"
					>
						{t('t5')} {'('} {filtersUsedCount} {')'}
					</h4>
					<div className="accordion-body bg-gray-100 px-3 py-2">
						<ResetFilterButton />
					</div>
				</>
			)}
		</>
	);
};

const FilterModal = ({ children }) => {
	const { t } = useTranslation('component_test_filters');

	return (
		<div
			aria-labelledby="staticBackdropLabel"
			className="offcanvas offcanvas-end modal"
			data-bs-scroll="true"
			id="staticBackdrop"
			tabIndex="-1"
		>
			<div className="modal-dialog modal-fullscreen">
				<div className="modal-content">
					<div className="modal-header">
						<h3 className="modal-title fs-5" id="staticBackdropLabel">
							{t('t3')}
						</h3>
						<button
							aria-label="Close"
							className="btn-close"
							data-bs-dismiss="offcanvas"
							type="button"
						></button>
					</div>
					<div className="modal-body">{children}</div>
					<div className="modal-footer">
						<button
							className="btn btn-light"
							data-bs-dismiss="offcanvas"
							type="button"
						>
							{t('t8')}
						</button>
						<button
							className="btn btn-primary"
							data-bs-dismiss="offcanvas"
							type="button"
						>
							{t('t9')}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

const FiltersList = ({ activeAccordion }) => {
	const { queryParameters, setQueryParameter } = useQueryParameters();
	const { t } = useTranslation('component_test_filters');

	const handleChange = (key, type, value) => (event) => {
		const checked = event.target.checked;

		if (type == 'array') {
			let new_settings = [];

			if (Array.isArray(queryParameters?.[key])) {
				new_settings = queryParameters?.[key];
			} else if (queryParameters?.[key]) {
				new_settings.push(queryParameters?.[key]);
			}

			if (checked) {
				new_settings.push(value);
			} else {
				new_settings = new_settings.filter((item) => item !== value);
			}

			setQueryParameter(key, new_settings);
		} else {
			setQueryParameter(key, checked);
		}
	};

	const handleIsChecked = (key, type, value) => {
		let checked = false;

		if (type == 'array') {
			checked = queryParameters?.[key]?.includes(value);
		} else {
			checked = queryParameters?.[key];
		}

		return checked;
	};

	return (
		<div className="accordion accordion-flush" id="tests-filters">
			{filtersConfig.map((group, index1) => (
				<div className="accordion-item" key={index1}>
					<h4 className="accordion-header" id={`header-${index1}`}>
						<button
							aria-controls={`panel-${index1}`}
							aria-expanded="false"
							className="accordion-button sm fw-700 collapsed"
							data-bs-target={`#panel-${index1}`}
							data-bs-toggle="collapse"
							type="button"
						>
							{t(group.groupName)}
						</button>
					</h4>
					<div
						aria-labelledby={`header-${index1}`}
						className={`accordion-collapse collapse ${activeAccordion === 'filter' ? 'show' : ''}`}
						id={`panel-${index1}`}
					>
						<div className="accordion-body bg-gray-100 px-3 py-2">
							{group.filters.map((filter, index2) => (
								<div className="form-check my-3" key={index2}>
									<input
										checked={handleIsChecked(
											filter.key,
											group.filter_type,
											filter.value,
										)}
										className="form-check-input"
										id={`${filter.key}_${index2}`}
										onChange={handleChange(
											filter.key,
											group.filter_type,
											filter.value,
										)}
										type="checkbox"
									/>
									<label
										className="form-check-label"
										htmlFor={`${filter.key}_${index2}`}
									>
										{t(filter.label)}
									</label>
								</div>
							))}
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

TestFilters.propTypes = {
	group_id: PropTypes.number,
};

FilterModal.propTypes = {
	children: PropTypes.node,
};

ChosenFilters.propTypes = {
	filtersUsedCount: PropTypes.number,
	isMobile: PropTypes.bool,
};

FilterButton.propTypes = {
	filtersUsedCount: PropTypes.number,
	label: PropTypes.string,
	onClick: PropTypes.func,
	type: PropTypes.string,
};

FiltersList.propTypes = {
	activeAccordion: PropTypes.string,
};

export default TestFilters;
